import * as React from "react";
import {
  Box,
  Stack,
  Typography,
  TextField,
  IconButton,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import FitnessCenterIcon from "@mui/icons-material/FitnessCenter";
import YouTubeIcon from "@mui/icons-material/YouTube";
import ViewListIcon from "@mui/icons-material/ViewList";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import GlassContainer from "../../../components/liquid-glass";
import VaultGridCard from "./VaultGridCard";
import { Exercise } from "../../../../../models/types";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../../../redux/store";
import { updateTrainerCustomExercises } from "../../../../../redux/thunks/thunk-partners";

import {
  CognitoUserPool,
  CognitoUserSession,
} from "amazon-cognito-identity-js";
import { poolData } from "../groups";

type Props = {
  exercises: Exercise[];
  onCreateExercise?: (ex: Exercise) => void; // now optional (we’ll dispatch ourselves)
  onUpdateExercise?: (ex: Exercise) => void; // optional
  onDeleteExercise?: (id?: string | null) => void; // optional
};

type SortKey = "name" | "type" | "muscleGroup";

const uid = () => Math.random().toString(36).slice(2) + Date.now().toString(36);

export default function ExercisesVaultList({
  exercises,
  onCreateExercise,
  onUpdateExercise,
  onDeleteExercise,
}: Props) {
  const dispatch = useDispatch<AppDispatch>();
  const darkMode = useSelector((s: RootState) => !!s.partners.darkMode);

  // Shared text colors for consistency
  const baseTextColor = darkMode ? "#f9fafb" : "#111827"; // light text in dark mode, dark in light mode
  const subtleTextColor = darkMode
    ? "rgba(249,250,251,0.7)"
    : "rgba(17,24,39,0.7)";

  // Filter option sets
  const typeOptions: string[] = [
    "barbell",
    "dumbbell",
    "machine",
    "bodyweight",
    "band",
    "cable",
    "kettlebell",
    "other",
  ];

  const muscleOptions: string[] = [
    "chest",
    "back",
    "shoulders",
    "biceps",
    "triceps",
    "legs",
    "glutes",
    "core",
    "full body",
    "other",
  ];

  // Current trainer user and existing customExercises from store
  const partner = useSelector(
    (s: RootState) => s.partners.partnerData?.partnerData
  );
  const trainerUserId = partner?.userId as string | undefined;
  const currentCustom = (partner?.customExercises ?? []) as Exercise[];

  const [q, setQ] = React.useState("");
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [editing, setEditing] = React.useState<Exercise | null>(null);

  const [viewMode, setViewMode] = React.useState<"grid" | "list">("grid");
  const [sortBy, setSortBy] = React.useState<SortKey>("name");
  const [sortDirection, setSortDirection] = React.useState<"asc" | "desc">(
    "asc"
  );

  // Chip filters
  const [typeFilter, setTypeFilter] = React.useState<string | null>(null);
  const [muscleFilter, setMuscleFilter] = React.useState<string | null>(null);

  // --- Auth helper to get idToken for the PUT call ---
  const getIdToken = React.useCallback(async (): Promise<string> => {
    return new Promise((resolve) => {
      try {
        const pool = new CognitoUserPool(poolData as any);
        const user = pool.getCurrentUser();
        if (!user) return resolve("");
        user.getSession((err: any, session: CognitoUserSession) => {
          if (err || !session?.isValid()) return resolve("");
          resolve(session.getIdToken().getJwtToken() ?? "");
        });
      } catch {
        resolve("");
      }
    });
  }, []);

  const text = (s?: string | null) =>
    (s ?? "")
      .toLowerCase()
      .normalize("NFD")
      .replace(/\p{Diacritic}/gu, "");

  const filtered = React.useMemo(() => {
    const t = text(q);

    const out = exercises.filter((e) => {
      // Search match
      let matchesSearch = true;
      if (t) {
        const hay = text(
          [
            e.name,
            e.type,
            e.muscleGroup,
            ...(e.detailedMusclesImpacted ?? []),
            ...(e.Instructions ?? []),
          ]
            .filter(Boolean)
            .join(" ")
        );
        matchesSearch = hay.includes(t);
      }

      // Type (equipment) filter
      const matchesType =
        !typeFilter ||
        (e.type ?? "").toLowerCase() === typeFilter.toLowerCase();

      // Muscle group (body part) filter
      const matchesMuscle =
        !muscleFilter ||
        (e.muscleGroup ?? "").toLowerCase() === muscleFilter.toLowerCase();

      return matchesSearch && matchesType && matchesMuscle;
    });

    // Default sort by name for grid view
    return out.sort((a, b) =>
      (a.name ?? "").localeCompare(b.name ?? "", undefined, {
        sensitivity: "base",
        numeric: true,
      })
    );
  }, [exercises, q, typeFilter, muscleFilter]);

  const sortedForList = React.useMemo(() => {
    const list = [...filtered];

    return list.sort((a, b) => {
      let aVal: string = "";
      let bVal: string = "";

      switch (sortBy) {
        case "name":
          aVal = (a.name ?? "").toLowerCase();
          bVal = (b.name ?? "").toLowerCase();
          break;
        case "type":
          aVal = (a.type ?? "").toLowerCase();
          bVal = (b.type ?? "").toLowerCase();
          break;
        case "muscleGroup":
          aVal = (a.muscleGroup ?? "").toLowerCase();
          bVal = (b.muscleGroup ?? "").toLowerCase();
          break;
        default:
          break;
      }

      const cmp = aVal.localeCompare(bVal);
      return sortDirection === "asc" ? cmp : -cmp;
    });
  }, [filtered, sortBy, sortDirection]);

  const handleSort = (key: SortKey) => {
    if (sortBy === key) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(key);
      setSortDirection("asc");
    }
  };

  const openCreate = () => {
    setEditing({
      id: null,
      name: "",
      type: null,
      muscleGroup: null,
      detailedMusclesImpacted: [],
      Instructions: [],
      shortYouTubeDemonstration: "",
      inDepthYouTubeExplanation: "",
    });
    setDialogOpen(true);
  };

  const openEdit = (ex: Exercise) => {
    setEditing({ ...ex });
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditing(null);
  };

  // Centralized updater → dispatch thunk to persist to DynamoDB
  const persistCustomExercises = React.useCallback(
    async (next: Exercise[]) => {
      if (!trainerUserId) return;
      const idToken = await getIdToken();
      if (!idToken) return;
      dispatch(
        updateTrainerCustomExercises({
          idToken,
          trainerUserId,
          customExercises: next,
        })
      );
    },
    [dispatch, getIdToken, trainerUserId]
  );

  const saveDialog = () => {
    if (!editing?.name?.trim() || !editing.type || !editing.muscleGroup) return;

    const withId: Exercise = {
      ...editing,
      id: editing.id ?? uid(),
      name: editing.name.trim(),
    };

    if (exercises.find((e) => e.id === withId.id)) {
      onUpdateExercise?.(withId);
      const next = currentCustom.map((e) => (e.id === withId.id ? withId : e));
      persistCustomExercises(next);
    } else {
      onCreateExercise?.(withId);
      const next = [...currentCustom, withId];
      persistCustomExercises(next);
    }
    closeDialog();
  };

  const deleteExercise = (id?: string | null) => {
    onDeleteExercise?.(id);
    if (!id) return;
    const next = currentCustom.filter((e) => e.id !== id);
    persistCustomExercises(next);
  };

  const handleDeleteClick = (
    ev: React.MouseEvent<HTMLButtonElement>,
    id?: string | null
  ) => {
    ev.stopPropagation();
    deleteExercise(id);
  };

  // Label color for all TextFields (consistent across modes)
  const labelColor = {
    sx: {
      color: `${baseTextColor} !important`,
      "&.Mui-focused": {
        color: `${baseTextColor} !important`,
      },
    },
  };

  // Shared select styling for filter dropdowns
  const selectSx = {
    minWidth: 140,
    "& .MuiInputBase-input": { color: baseTextColor },
    "& .MuiSvgIcon-root": { color: subtleTextColor },
  } as const;

  const isSaveDisabled =
    !editing?.name?.trim() || !editing.type || !editing.muscleGroup;

  return (
    <Box sx={{ mt: 0, pt: 0 }}>
      {/* ── Unified toolbar ── */}
      <Stack
        direction="row"
        spacing={1.5}
        mb={2}
        alignItems="center"
        flexWrap="wrap"
        sx={{ rowGap: 1.5 }}
      >
        {/* Search */}
        <TextField
          value={q}
          onChange={(e) => setQ(e.target.value)}
          sx={{
            flex: 1,
            minWidth: 200,
            maxWidth: { xs: "100%", sm: "38%" },
            "& .MuiInputBase-input": { color: baseTextColor },
          }}
          placeholder='Search exercises…'
          size="small"
          label="Search"
          InputLabelProps={{
            sx: {
              color: baseTextColor,
              "&.Mui-focused": { color: baseTextColor },
            },
          }}
        />

        {/* Equipment dropdown */}
        <FormControl size="small" sx={selectSx}>
          <InputLabel
            sx={{
              color: baseTextColor,
              "&.Mui-focused": { color: baseTextColor },
            }}
          >
            Equipment
          </InputLabel>
          <Select
            value={typeFilter ?? ""}
            label="Equipment"
            onChange={(e: SelectChangeEvent) =>
              setTypeFilter(e.target.value === "" ? null : e.target.value)
            }
          >
            <MenuItem value="">All</MenuItem>
            {typeOptions.map((t) => (
              <MenuItem key={t} value={t} sx={{ textTransform: "capitalize" }}>
                {t}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Body Part dropdown */}
        <FormControl size="small" sx={selectSx}>
          <InputLabel
            sx={{
              color: baseTextColor,
              "&.Mui-focused": { color: baseTextColor },
            }}
          >
            Body Part
          </InputLabel>
          <Select
            value={muscleFilter ?? ""}
            label="Body Part"
            onChange={(e: SelectChangeEvent) =>
              setMuscleFilter(e.target.value === "" ? null : e.target.value)
            }
          >
            <MenuItem value="">All</MenuItem>
            {muscleOptions.map((mg) => (
              <MenuItem key={mg} value={mg} sx={{ textTransform: "capitalize" }}>
                {mg}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Spacer pushes view toggles + action to the right */}
        <Box sx={{ flex: "1 0 0", display: { xs: "none", md: "block" } }} />

        {/* View toggles */}
        <Stack direction="row" spacing={0.5} alignItems="center">
          {viewMode === "list" ? (
            <GlassContainer
              blur={18}
              radius={999}
              sx={{ p: 0.25 }}
              dontHoverPop
            >
              <IconButton size="small" onClick={() => setViewMode("list")} color="primary">
                <ViewListIcon fontSize="small" />
              </IconButton>
            </GlassContainer>
          ) : (
            <IconButton
              size="small"
              onClick={() => setViewMode("list")}
              color="default"
              sx={{ color: darkMode ? "rgba(249,250,251,0.65)" : "#111827" }}
            >
              <ViewListIcon fontSize="small" />
            </IconButton>
          )}

          {viewMode === "grid" ? (
            <GlassContainer
              blur={18}
              radius={999}
              sx={{ p: 0.25 }}
              dontHoverPop
            >
              <IconButton size="small" onClick={() => setViewMode("grid")} color="primary">
                <ViewModuleIcon fontSize="small" />
              </IconButton>
            </GlassContainer>
          ) : (
            <IconButton
              size="small"
              onClick={() => setViewMode("grid")}
              color="default"
              sx={{ color: darkMode ? "rgba(249,250,251,0.65)" : "#111827" }}
            >
              <ViewModuleIcon fontSize="small" />
            </IconButton>
          )}
        </Stack>

        {/* + New Exercise */}
        <GlassContainer
          blur={18}
          radius={999}
          sx={{ pl: 1.25, pr: 1.75, py: 0.4 }}
          dontHoverPop
        >
          <Button
            variant="text"
            startIcon={<AddIcon />}
            onClick={openCreate}
            disabled={!trainerUserId}
            title={!trainerUserId ? "No trainer account loaded" : undefined}
            sx={{
              textTransform: "none",
              px: 0,
              minWidth: 0,
              color: baseTextColor,
            }}
          >
            New Exercise
          </Button>
        </GlassContainer>
      </Stack>

      {filtered.length === 0 ? (
        <Typography
          variant="body2"
          color={darkMode ? "inherit" : "text.secondary"}
          sx={{ mt: 0, color: subtleTextColor }}
        >
          No exercises found. Try adjusting filters or adding one!
        </Typography>
      ) : viewMode === "list" ? (
        <GlassContainer
          blur={18}
          radius={14}
          sx={{
            mt: 0,
            overflow: "hidden",
          }}
        >
          <TableContainer
            sx={{
              maxHeight: 600,
              backgroundColor: "transparent",
            }}
          >
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow
                  sx={{
                    "& th": {
                      borderBottom: "1px solid",
                      borderBottomColor: darkMode
                        ? "rgba(255,255,255,0.12)"
                        : "rgba(0,0,0,0.06)",
                    },
                  }}
                >
                  <TableCell
                    sx={{
                      backgroundColor: darkMode
                        ? "rgba(255,255,255,0.04)"
                        : "grey.50",
                      color: baseTextColor,
                      fontWeight: 500,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                      }}
                    >
                      <FitnessCenterIcon
                        fontSize="small"
                        sx={{ color: subtleTextColor }}
                      />
                      <TableSortLabel
                        active={sortBy === "name"}
                        direction={sortBy === "name" ? sortDirection : "asc"}
                        onClick={() => handleSort("name")}
                        sx={{
                          "&.Mui-active": {
                            color: `${baseTextColor} !important`,
                          },
                          "& .MuiTableSortLabel-icon": {
                            color: `${baseTextColor} !important`,
                          },
                        }}
                      >
                        Name
                      </TableSortLabel>
                    </Box>
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: darkMode
                        ? "rgba(255,255,255,0.04)"
                        : "grey.50",
                      color: baseTextColor,
                      fontWeight: 500,
                    }}
                  >
                    <TableSortLabel
                      active={sortBy === "type"}
                      direction={sortBy === "type" ? sortDirection : "asc"}
                      onClick={() => handleSort("type")}
                      sx={{
                        "&.Mui-active": {
                          color: `${baseTextColor} !important`,
                        },
                        "& .MuiTableSortLabel-icon": {
                          color: `${baseTextColor} !important`,
                        },
                      }}
                    >
                      Type
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: darkMode
                        ? "rgba(255,255,255,0.04)"
                        : "grey.50",
                      color: baseTextColor,
                      fontWeight: 500,
                    }}
                  >
                    <TableSortLabel
                      active={sortBy === "muscleGroup"}
                      direction={
                        sortBy === "muscleGroup" ? sortDirection : "asc"
                      }
                      onClick={() => handleSort("muscleGroup")}
                      sx={{
                        "&.Mui-active": {
                          color: `${baseTextColor} !important`,
                        },
                        "& .MuiTableSortLabel-icon": {
                          color: `${baseTextColor} !important`,
                        },
                      }}
                    >
                      Muscle Group
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      backgroundColor: darkMode
                        ? "rgba(255,255,255,0.04)"
                        : "grey.50",
                      color: baseTextColor,
                      fontWeight: 500,
                    }}
                  >
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedForList.map((e) => (
                  <TableRow
                    key={e.id ?? e.name}
                    hover
                    onClick={() => openEdit(e)}
                    sx={{
                      cursor: "pointer",
                      "& .MuiTableCell-root": {
                        py: 1.25,
                        borderBottom: "1px solid",
                        borderBottomColor: darkMode
                          ? "rgba(255,255,255,0.12)"
                          : "rgba(0,0,0,0.06)",
                      },
                    }}
                  >
                    <TableCell
                      sx={{
                        color: baseTextColor,
                        fontWeight: 500,
                      }}
                    >
                      {e.name}
                    </TableCell>
                    <TableCell
                      sx={{
                        color: subtleTextColor,
                      }}
                    >
                      {e.type || "—"}
                    </TableCell>
                    <TableCell
                      sx={{
                        color: subtleTextColor,
                      }}
                    >
                      {e.muscleGroup || "—"}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={(ev) => handleDeleteClick(ev, e.id)}
                        color="error"
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </GlassContainer>
      ) : (
        <Box
          sx={{
            display: "grid",
            gap: 1,
            gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
          }}
        >
          {filtered.map((e) => (
            <VaultGridCard
              key={e.id ?? e.name}
              onClick={() => openEdit(e)}
              minHeight={176}
              title={e.name || "Untitled exercise"}
              titleIcon={
                <FitnessCenterIcon
                  sx={{ opacity: 0.8, color: subtleTextColor, fontSize: 18 }}
                />
              }
              rightAction={
                <IconButton
                  size="small"
                  onClick={(ev) => handleDeleteClick(ev, e.id)}
                  color="error"
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              }
              chips={
                <>
                  {e.type && (
                    <Chip size="small" label={e.type} sx={{ color: baseTextColor }} />
                  )}
                  {e.muscleGroup && (
                    <Chip
                      size="small"
                      label={e.muscleGroup}
                      sx={{ color: baseTextColor }}
                    />
                  )}
                  {(e.detailedMusclesImpacted ?? []).slice(0, 3).map((m) => (
                    <Chip
                      key={m}
                      size="small"
                      label={m}
                      variant="outlined"
                      sx={{ color: subtleTextColor }}
                    />
                  ))}
                </>
              }
              body={
                e.shortYouTubeDemonstration || e.inDepthYouTubeExplanation ? (
                  <>
                    <Divider sx={{ my: 1, borderColor: subtleTextColor }} />
                    <Stack direction="row" spacing={1} alignItems="center">
                      <YouTubeIcon sx={{ opacity: 0.7, color: subtleTextColor }} />
                      {e.shortYouTubeDemonstration && (
                        <Chip
                          size="small"
                          clickable
                          label="Short demo"
                          onClick={(ev) => {
                            ev.stopPropagation();
                            window.open(
                              ensureHttp(e.shortYouTubeDemonstration!),
                              "_blank",
                              "noopener,noreferrer"
                            );
                          }}
                          sx={{ color: baseTextColor }}
                        />
                      )}
                      {e.inDepthYouTubeExplanation && (
                        <Chip
                          size="small"
                          clickable
                          label="In-depth"
                          onClick={(ev) => {
                            ev.stopPropagation();
                            window.open(
                              ensureHttp(e.inDepthYouTubeExplanation!),
                              "_blank",
                              "noopener,noreferrer"
                            );
                          }}
                          sx={{ color: baseTextColor }}
                        />
                      )}
                    </Stack>
                  </>
                ) : (
                  <Typography
                    sx={{
                      fontSize: "0.72rem",
                      color: subtleTextColor,
                      fontStyle: "italic",
                    }}
                  >
                    No demo links
                  </Typography>
                )
              }
            />
          ))}
        </Box>
      )
      }

      <Dialog
        open={dialogOpen}
        onClose={closeDialog}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            backgroundColor: "transparent",
            boxShadow: "none",
          },
        }}
      >
        <GlassContainer
          blur={22}
          radius={18}
          sx={{
            p: 2,
            backgroundColor: darkMode
              ? "rgba(50,50,50,0.9)"
              : "rgba(255,255,255,0.8)",
          }}
          dontHoverPop
        >
          <DialogTitle
            sx={{
              color: baseTextColor,
              pb: 1,
            }}
          >
            {editing?.id ? "Edit Exercise" : "New Exercise"}
          </DialogTitle>
          <DialogContent>
            <Stack spacing={1} mt={1}>
              <TextField
                autoFocus
                label="Exercise name *"
                value={editing?.name ?? ""}
                onChange={(e) =>
                  setEditing((x) => ({
                    ...(x as Exercise),
                    name: e.target.value,
                  }))
                }
                size="small"
                InputLabelProps={labelColor}
                sx={{
                  "& .MuiInputBase-input": {
                    color: baseTextColor,
                  },
                }}
              />
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                <TextField
                  label="Type *"
                  value={editing?.type ?? ""}
                  onChange={(e) =>
                    setEditing((x) => ({
                      ...(x as Exercise),
                      type: e.target.value,
                    }))
                  }
                  size="small"
                  select
                  sx={{
                    minWidth: 160,
                    "& .MuiInputBase-input": {
                      color: baseTextColor,
                    },
                  }}
                  InputLabelProps={labelColor}
                >
                  {typeOptions.map((t) => (
                    <MenuItem key={t} value={t}>
                      {t}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  label="Muscle group *"
                  value={editing?.muscleGroup ?? ""}
                  onChange={(e) =>
                    setEditing((x) => ({
                      ...(x as Exercise),
                      muscleGroup: e.target.value,
                    }))
                  }
                  size="small"
                  select
                  sx={{
                    minWidth: 160,
                    "& .MuiInputBase-input": {
                      color: baseTextColor,
                    },
                  }}
                  InputLabelProps={labelColor}
                >
                  {muscleOptions.map((mg) => (
                    <MenuItem key={mg} value={mg}>
                      {mg}
                    </MenuItem>
                  ))}
                </TextField>
              </Stack>
              <TextField
                label="Short demo (YouTube URL)"
                value={editing?.shortYouTubeDemonstration ?? ""}
                onChange={(e) =>
                  setEditing((x) => ({
                    ...(x as Exercise),
                    shortYouTubeDemonstration: e.target.value,
                  }))
                }
                size="small"
                InputLabelProps={labelColor}
                sx={{
                  "& .MuiInputBase-input": {
                    color: baseTextColor,
                  },
                }}
              />
              <TextField
                label="In-depth demo (YouTube URL)"
                value={editing?.inDepthYouTubeExplanation ?? ""}
                onChange={(e) =>
                  setEditing((x) => ({
                    ...(x as Exercise),
                    inDepthYouTubeExplanation: e.target.value,
                  }))
                }
                size="small"
                InputLabelProps={labelColor}
                sx={{
                  "& .MuiInputBase-input": {
                    color: baseTextColor,
                  },
                }}
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeDialog}>Cancel</Button>
            <Button
              variant="contained"
              onClick={saveDialog}
              disabled={isSaveDisabled}
            >
              Save
            </Button>
          </DialogActions>
        </GlassContainer>
      </Dialog>
    </Box >
  );
}

function ensureHttp(u?: string | null) {
  const v = (u ?? "").trim();
  if (!v) return "";
  return /^https?:\/\//i.test(v) ? v : `https://${v}`;
}
